import {
  CreateLearningPathDto,
  LearningPath,
  PathLevel,
} from '../../../../core/interfaces/learning-path';
import { Subject } from '../../../../core/interfaces/subject';

/** Un cycle scolaire, ou `none` pour les parcours qui n'en relèvent pas. */
export type CycleKey = PathLevel | 'none';

/** L'ordre des cycles à l'écran : celui de la scolarité, les « sans cycle » en dernier. */
export const CYCLES: readonly CycleKey[] = ['Primaire', 'College', 'Secondaire', 'none'];

/** Clé i18n de l'intitulé d'un cycle. Table explicite : `Primaire` se dit `LEVEL_PRIMARY`. */
export const CYCLE_LABEL_KEYS: Record<CycleKey, string> = {
  Primaire: 'ADMIN.LEVEL_PRIMARY',
  College: 'ADMIN.LEVEL_COLLEGE',
  Secondaire: 'ADMIN.LEVEL_SECONDARY',
  none: 'ADMIN.LEVEL_NONE',
};

/** Un parcours dans la liste, avec ce qu'elle en dit sans l'ouvrir. */
export interface PathEntry {
  path: LearningPath;
  /** Matières en vente et proposées dans l'application. */
  activeSubjects: number;
  totalSubjects: number;
}

export interface CycleGroup {
  cycle: CycleKey;
  paths: PathEntry[];
}

/** Les champs d'un parcours tels que le formulaire les tient. */
export interface PathDraft {
  title: string;
  titleAr: string;
  titleFr: string;
  titleEn: string;
  level: PathLevel | null;
  /**
   * `published`, `draft` — ou toute autre valeur déjà en base, gardée telle
   * quelle : corriger un titre ne doit pas dépublier un parcours dont le
   * statut ne serait pas l'un des deux que le formulaire propose.
   */
  status: string;
  /** `null` = case vide : la durée est facultative. */
  durationHours: number | null;
}

/** Les champs d'une matière tels que le formulaire les tient. */
export interface SubjectDraft {
  name: string;
  nameAr: string;
  nameFr: string;
  nameEn: string;
  displayOrder: number | null;
  isActive: boolean;
}

const TITLE_MIN = 3;
const TITLE_MAX = 255;
const DURATION_MAX = 1000;

/**
 * Le cycle d'un parcours, lu sans égard à la casse : le serveur écrit
 * « Primaire », la colonne stocke « primaire », et une lecture stricte
 * rangerait la moitié des parcours dans « sans cycle ».
 */
export function cycleOf(path: Pick<LearningPath, 'level'>): CycleKey {
  const level = (path.level ?? '').toString().toLowerCase();
  return CYCLES.find((c) => c.toLowerCase() === level && c !== 'none') ?? 'none';
}

/**
 * Les parcours groupés par cycle, filtrés par la recherche.
 *
 * <p>La recherche porte sur le titre et ses trois traductions : un
 * administrateur qui lit l'arabe cherche « السنة », pas « année ». Vide, elle
 * ne filtre rien — c'est le défaut de tout filtre de l'administration.</p>
 */
export function groupByCycle(paths: readonly LearningPath[], search: string): CycleGroup[] {
  const needle = search.trim().toLowerCase();
  const matches = (path: LearningPath): boolean =>
    needle === '' ||
    [path.title, path.titleAr, path.titleFr, path.titleEn].some((t) =>
      (t ?? '').toLowerCase().includes(needle),
    );

  return CYCLES.map((cycle) => ({
    cycle,
    paths: paths
      .filter((p) => cycleOf(p) === cycle && matches(p))
      .map((path) => ({
        path,
        activeSubjects: (path.subjects ?? []).filter((s) => s.isActive === true).length,
        totalSubjects: (path.subjects ?? []).length,
      })),
  })).filter((g) => g.paths.length > 0);
}

/** Les matières dans l'ordre d'affichage ; à ordre égal, par nom, pour un rendu stable. */
export function sortSubjects(subjects: readonly Subject[] | undefined): Subject[] {
  return [...(subjects ?? [])].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.name.localeCompare(b.name),
  );
}

export function pathDraftOf(path?: LearningPath | null): PathDraft {
  return {
    title: path?.title ?? '',
    titleAr: path?.titleAr ?? '',
    titleFr: path?.titleFr ?? '',
    titleEn: path?.titleEn ?? '',
    level: path ? (cycleOf(path) === 'none' ? null : (cycleOf(path) as PathLevel)) : null,
    // Un parcours naît brouillon : le publier le met en vente sur le site.
    status: path?.status ?? 'draft',
    durationHours: path?.durationHours ?? null,
  };
}

export function sameDraft<T extends object>(a: T, b: T): boolean {
  return (Object.keys(a) as (keyof T)[]).every((k) => a[k] === b[k]);
}

/** Clé i18n de la première erreur du parcours, ou `null` s'il peut partir. */
export function pathDraftError(draft: PathDraft): string | null {
  const title = draft.title.trim();
  if (title.length < TITLE_MIN || title.length > TITLE_MAX) return 'PATH_SUBJECTS.TITLE_INVALID';
  if (
    draft.durationHours != null &&
    (!Number.isInteger(draft.durationHours) || draft.durationHours < 0 || draft.durationHours > DURATION_MAX)
  ) {
    return 'PATH_SUBJECTS.DURATION_INVALID';
  }
  return null;
}

export function toPathDto(draft: PathDraft): CreateLearningPathDto {
  return {
    title: draft.title.trim(),
    titleAr: draft.titleAr.trim() || null,
    titleFr: draft.titleFr.trim() || null,
    titleEn: draft.titleEn.trim() || null,
    status: draft.status as CreateLearningPathDto['status'],
    level: draft.level,
    // Une durée vide reste vide : l'écrire à zéro changerait une valeur que
    // personne n'a saisie.
    durationHours: draft.durationHours,
  };
}

export function subjectDraftOf(subject: Subject | null, nextOrder: number): SubjectDraft {
  return {
    name: subject?.name ?? '',
    nameAr: subject?.nameAr ?? '',
    nameFr: subject?.nameFr ?? '',
    nameEn: subject?.nameEn ?? '',
    displayOrder: subject ? (subject.displayOrder ?? 0) : nextOrder,
    // Une matière naît active : c'est pour la vendre qu'on l'ajoute.
    isActive: subject ? subject.isActive === true : true,
  };
}

/** Clé i18n de la première erreur de la matière, ou `null` si elle peut partir. */
export function subjectDraftError(draft: SubjectDraft): string | null {
  const name = draft.name.trim();
  if (name.length < TITLE_MIN || name.length > TITLE_MAX) return 'PATH_SUBJECTS.NAME_INVALID';
  if (
    draft.displayOrder == null ||
    !Number.isInteger(draft.displayOrder) ||
    draft.displayOrder < 0
  ) {
    return 'PATH_SUBJECTS.ORDER_INVALID';
  }
  return null;
}

/** L'ordre proposé à une nouvelle matière : à la suite des autres. */
export function nextSubjectOrder(subjects: readonly Subject[] | undefined): number {
  return (subjects ?? []).reduce((max, s) => Math.max(max, s.displayOrder ?? 0), 0) + 1;
}
